import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { FC } from 'react';
import { useCaregiverItem } from '@views/pages/Patient/components/CareTeam/components/hooks';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  DatepickerFormField,
  SelectFormField,
  TextFormField,
  CheckboxFormField,
  CustomModal,
  SaveChangesModal,
  USStateSelect,
} from '@components';
import dayjs from 'dayjs';
import { FormGooglePlacesAutocomplete } from '@components';
import { theme } from '@store';
import { isNil } from 'lodash';

interface AddCaregiverProps {
  patientId: string;
  disabled: boolean;
}

export const CaregiverItem: FC<AddCaregiverProps> = ({
  patientId,
  disabled = false,
}) => {
  const {
    isOpen,
    handleOpen,
    handleClose,
    formMethods,
    onSubmit,
    genderOptions,
    raceOptions,
    ethnicityOptions,
    relationshipOptions,
    willingnessOptions,
    phoneTypeOptions,
    isSubmitting,
    handleSelectLocation,
    confirmationRadioButtonOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCaregiverItem(patientId);

  const { control } = formMethods;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t('patient.careTeam.noPrimaryCaregiver')}
        </Typography>
        <Button
          variant='outlined'
          sx={{ height: '30px' }}
          onClick={handleOpen}
          disabled={disabled}
        >
          {t('patient.careTeam.addCaregiver')}
        </Button>
      </Box>

      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t(`patient.careTeam.modal.addCaregiver.title.create`)}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='first_name'
                label={t('patient.careTeam.modal.labels.firstName')}
              />

              <TextFormField
                name='middle_name'
                label={t('patient.careTeam.modal.labels.middleName')}
                optional
              />
            </Box>
            <TextFormField
              name='last_name'
              label={t('patient.careTeam.modal.labels.lastName')}
            />
            <DatepickerFormField
              name='birthdate'
              label={t('patient.careTeam.modal.labels.birthdate')}
              optional
              maxDate={dayjs(new Date())}
            />
            <SelectFormField
              name='gender'
              label={t('patient.careTeam.modal.labels.gender')}
              optional
              options={genderOptions}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <SelectFormField
                name='race'
                label={t('patient.careTeam.modal.labels.race')}
                optional
                options={raceOptions}
              />
              <SelectFormField
                name='ethnicity'
                label={t('patient.careTeam.modal.labels.ethnicity')}
                optional
                options={ethnicityOptions}
              />
            </Box>
            <SelectFormField
              name='relationship'
              label={t('patient.careTeam.modal.labels.relationship')}
              options={relationshipOptions}
            />

            <Box>
              <Typography variant='body1'>
                {t('patient.careTeam.modal.labels.caregiverPatient')}
              </Typography>
              <Controller
                name='lives_with_patient'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl sx={{ width: '100%' }} error={!!error}>
                    <RadioGroup row value={field.value ?? ''}>
                      {confirmationRadioButtonOptions.map(option => (
                        <FormControlLabel
                          id={option.value}
                          key={option.value}
                          value={option.value}
                          onClick={() => {
                            field.onChange(
                              option.value === field.value ? '' : option.value,
                            );
                          }}
                          control={<Radio />}
                          label={option.label}
                          htmlFor={option.value}
                        />
                      ))}
                    </RadioGroup>
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <Box>
              <Typography variant='body1'>
                {t('patient.careTeam.modal.labels.caregiverMedicare')}
              </Typography>
              <Controller
                name='has_medicare'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl sx={{ width: '100%' }} error={!!error}>
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        key={'yes'}
                        value={'yes'}
                        control={<Radio value={'yes'} />}
                        label={t('common.yes')}
                      />
                      <FormControlLabel
                        key={'no'}
                        value={'no'}
                        control={<Radio value={'no'} />}
                        label={t('common.no')}
                      />
                    </RadioGroup>
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <DatepickerFormField
              name='start_date'
              label={t('patient.careTeam.modal.labels.startedTheRole')}
              optional
            />

            <Typography variant='subtitle2'>
              {t('patient.careTeam.modal.addCaregiver.subTitle')}
            </Typography>

            <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='street'
                optional
                label={t('patient.careTeam.modal.labels.addressLine', {
                  number: 1,
                })}
              />

              <TextFormField
                name='suite'
                label={t('patient.careTeam.modal.labels.addressLine', {
                  number: 2,
                })}
                optional
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    value={field.value ?? ''}
                    error={!isNil(error)}
                    helperText={error ? error.message : ''}
                  />
                )}
              />

              <TextFormField
                name='city'
                optional
                label={t('patient.careTeam.modal.labels.city')}
              />
            </Box>

            <TextFormField
              name='zip_code'
              optional
              label={t('patient.careTeam.modal.labels.zip')}
            />
            <TextFormField
              name='country'
              label={t('patient.careTeam.modal.labels.country')}
              optional
            />
            <TextFormField
              name='email'
              label={t('patient.careTeam.modal.labels.email')}
              optional
            />

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextFormField
                name='phone_number'
                label={t('patient.careTeam.modal.labels.phone')}
              />

              <SelectFormField
                name='phone_type'
                label={t('patient.careTeam.modal.labels.phoneType')}
                options={phoneTypeOptions}
              />
            </Box>

            <Box>
              <Typography variant='body1'>
                {t('patient.careTeam.modal.labels.willingness')}
              </Typography>
              <Controller
                name='willingness'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl sx={{ width: '100%' }} error={!!error}>
                    <RadioGroup {...field}>
                      {willingnessOptions.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio value={option.value} />}
                          onClick={() => {
                            field.onChange(
                              option.value === field.value ? '' : option.value,
                            );
                          }}
                          label={option.label}
                          htmlFor={option.value}
                        />
                      ))}
                    </RadioGroup>
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <Typography variant='body1'>
              {t('patient.careTeam.modal.labels.role')}
            </Typography>
            <CheckboxFormField
              name='primaryCaregiver'
              label={t('patient.careTeam.modal.labels.primaryCaregiver')}
              optional
              disabled
            />
            <CheckboxFormField
              name='emergencyContact'
              optional
              label={t('patient.careTeam.modal.labels.emergencyContact')}
            />
            <CheckboxFormField
              name='powerOfAttorney'
              optional
              label={t('patient.careTeam.modal.labels.powerOfAttorney')}
            />
            <CheckboxFormField
              name='guardian'
              optional
              label={t('patient.careTeam.modal.labels.guardian')}
            />
            <CheckboxFormField
              name='other'
              optional
              label={t('patient.careTeam.modal.labels.other')}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
